import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'throttle-debounce';
import { useDispatch, useSelector } from 'react-redux';
import { selectFiltersPosition } from '../../../selectors/filters';
import { setFiltersPosition } from '../../../actions/filters/position';
import { EPosition } from '../../../types/filters';
import { setOpenedFilter } from '../../../actions/filters';

export const useScroll = (filtersRef: RefObject<HTMLDivElement>) => {
  const dispatch = useDispatch();
  const scrollTop = useRef<number>(0);
  const [staticOffsetTop, setStaticOffsetTop] = useState<number>(0);
  const position = useSelector(selectFiltersPosition);

  const handleScroll = useMemo(
    () =>
      throttle(50, () => {
        /* istanbul ignore next */
        if (!filtersRef.current) {
          return;
        }

        const { height: filtersHeight } = filtersRef.current.getBoundingClientRect();

        const newScrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const areFiltersVisible = newScrollTop <= staticOffsetTop + filtersHeight;
        const scrollDirection = newScrollTop > scrollTop.current ? 'down' : 'up';
        scrollTop.current = newScrollTop;

        if (areFiltersVisible) {
          if (newScrollTop <= staticOffsetTop) {
            dispatch(setFiltersPosition(EPosition.Static));
          }
        } else {
          dispatch(setOpenedFilter(undefined));

          if (scrollDirection === 'up') {
            dispatch(setFiltersPosition(EPosition.Fixed));
          } else if (position !== 'static') {
            dispatch(setFiltersPosition(EPosition.FixedHidden));
          }
        }
      }),
    [filtersRef, staticOffsetTop, dispatch, position],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [handleScroll]);

  /**
   * Вычисляет отступ фильтров от начала документа,
   * чтобы переводит в состояние `static` в нужный момент.
   */
  useEffect(() => {
    /* istanbul ignore next */
    if (!filtersRef.current) {
      return;
    }

    const filtersOffsetTop = getFiltersOffsetTop(filtersRef.current);

    setStaticOffsetTop(filtersOffsetTop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return position;
};

export function getFiltersOffsetTop(elem: HTMLElement) {
  const box = elem.getBoundingClientRect();

  const body = document.body;
  const docEl = document.documentElement;

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;

  const clientTop = docEl.clientTop || body.clientTop || 0;

  const top = box.top + scrollTop - clientTop;

  return Math.round(top);
}
